import { useState, useEffect, useCallback } from "react";
import { Plus, Search, Shield, Edit2, Trash2 } from "lucide-react";
import api from "../../services/api";
import PolicyForm from "./PolicyForm";
import { useAuth } from "../../context/AuthContext";

function fmtCurrency(v) {
  if (!v) return "R$ 0";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

var TYPE_LABELS = { auto: "Auto", residencial: "Residencial", vida: "Vida", empresarial: "Empresarial" };
var STATUS_CLS = { ativa: "bg-emerald-100 text-emerald-700", vencida: "bg-red-100 text-red-700", em_renovacao: "bg-amber-100 text-amber-700", cancelada: "bg-gray-100 text-gray-500" };
var STATUS_LABELS = { ativa: "Ativa", vencida: "Vencida", em_renovacao: "Em Renovacao", cancelada: "Cancelada" };

function PolicyRow({ policy, onEdit, onDelete, isAdmin }) {
  var typeLbl = TYPE_LABELS[policy.insurance_type] || policy.insurance_type;
  var statusCls = STATUS_CLS[policy.status] || "bg-gray-100 text-gray-500";
  var statusLbl = STATUS_LABELS[policy.status] || policy.status;

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50">
      <td className="py-3 px-4 text-sm font-mono text-[#0A2F3D]">{policy.policy_number}</td>
      <td className="py-3 px-4 text-sm text-gray-700">{policy.client_name}</td>
      <td className="py-3 px-4 text-sm text-gray-700">{policy.insurer_name}</td>
      <td className="py-3 px-4"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{typeLbl}</span></td>
      <td className="py-3 px-4 text-sm font-semibold text-emerald-600">{fmtCurrency(policy.premium)}</td>
      <td className="py-3 px-4 text-xs text-gray-500">{policy.start_date} a {policy.end_date}</td>
      <td className="py-3 px-4"><span className={"text-xs font-medium px-2 py-0.5 rounded-full " + statusCls}>{statusLbl}</span></td>
      <td className="py-3 px-4">
        {isAdmin && (
          <div className="flex gap-1">
            <button onClick={function() { onEdit(policy); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-[#3B82F6]" data-testid={"edit-policy-" + policy.id}><Edit2 className="w-4 h-4" /></button>
            <button onClick={function() { onDelete(policy.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={"delete-policy-" + policy.id}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function PoliciesTab() {
  var auth = useAuth();
  var isAdmin = auth.user && auth.user.role === "admin";
  var policiesState = useState([]);
  var policies = policiesState[0];
  var setPolicies = policiesState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var showFormState = useState(false);
  var showForm = showFormState[0];
  var setShowForm = showFormState[1];
  var editingState = useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];
  var searchState = useState("");
  var search = searchState[0];
  var setSearch = searchState[1];
  var filterState = useState("");
  var filterType = filterState[0];
  var setFilterType = filterState[1];

  var fetchPolicies = useCallback(function() {
    setLoading(true);
    var qs = "?limit=100";
    if (search) qs += "&search=" + encodeURIComponent(search);
    if (filterType) qs += "&insurance_type=" + filterType;
    api.get("/policies" + qs).then(function(res) {
      setPolicies(res.data.policies || []);
    }).finally(function() { setLoading(false); });
  }, [search, filterType]);

  useEffect(function() { fetchPolicies(); }, [fetchPolicies]);

  function handleSave() { setShowForm(false); setEditing(null); fetchPolicies(); }
  function handleEdit(p) { setEditing(p); setShowForm(true); }
  function handleDelete(id) {
    if (!window.confirm("Excluir esta apolice?")) return;
    api.delete("/policies/" + id).then(function() { fetchPolicies(); });
  }

  if (showForm) {
    return <PolicyForm policy={editing} onSave={handleSave} onCancel={function() { setShowForm(false); setEditing(null); }} />;
  }

  return (
    <div data-testid="policies-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Buscar apolice..." className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56" data-testid="search-policies" />
          </div>
          <select value={filterType} onChange={function(e) { setFilterType(e.target.value); }} className="px-3 py-2 border rounded-lg text-sm" data-testid="filter-type">
            <option value="">Todos os tipos</option>
            <option value="auto">Auto</option>
            <option value="residencial">Residencial</option>
            <option value="vida">Vida</option>
            <option value="empresarial">Empresarial</option>
          </select>
        </div>
        {isAdmin && (
          <button onClick={function() { setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors" data-testid="new-policy-btn">
            <Plus className="w-4 h-4" />Nova Apolice
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : policies.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Nenhuma apolice cadastrada</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Numero</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Seguradora</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Premio</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Vigencia</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {policies.map(function(p) {
                return <PolicyRow key={p.id} policy={p} onEdit={handleEdit} onDelete={handleDelete} isAdmin={isAdmin} />;
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
